/**
 * engine/progress.ts — `updateFromProgress` adapter + EMA smoothing.
 *
 * Owns the progress-tick → reactive state transformation:
 *
 *   - EMA smoothing for download/upload speeds (EMA_ALPHA = 0.15)
 *   - Per-metric lifecycle transitions (notStarted → started → active → finished)
 *   - Phase-boundary fire bookkeeping (each phase boundary fires once per run)
 *   - Boundary callbacks via `submitPhaseSnapshot`
 *
 * The factory `createProgressTracker` returns a small state-bag the
 * subscription module composes against. State is closure-private; the
 * subscription module never touches `smoothedDownload` / `smoothedUpload`
 * / `phaseBoundaryFired` directly.
 */

import { ref, type Ref } from "vue";

import type { ProgressData } from "@speedtest/index.ts";

import { TestState } from "../types.ts";
import type { LiveTestData, MetricStates, PhaseCompletePayload } from "../types.ts";

/** EMA smoothing factor (0–1). Higher = more responsive, lower = smoother.
 *  Kept low because both downstream consumers — the canvas needle (via
 *  SmoothProgress in useMeterRenderer) and the DOM number (via
 *  useAnimatedNumber) — apply their own exponential damping. This EMA
 *  layer just trims raw spikes before they enter either smoother. */
export const EMA_ALPHA = 0.15;

/**
 * KEEP-with-DDR (AV.W11b suffusion — KEEP #3 of 3; see
 * docs/decisions/DDR-AV-W11B-SUFFUSION.md "Timing lane"). This single-pole
 * EMA is an ENGINE-LAYER data pre-filter — it trims raw speed-sample spikes
 * before they enter EITHER render-time smoother (the canvas needle's
 * `SmoothProgress`, the DOM number's `useAnimatedNumber`). Migrating it onto
 * keyframes.js `SmoothProgress` would couple the ENGINE (the speedtest
 * measurement core) to a render-animation library — the wrong direction
 * across the W2 boundary model (engine produces data; render animates it).
 * It is three tokens of well-understood arithmetic, not a hand-rolled
 * re-implementation of a library primitive. It correctly stays here.
 */
export function ema(prev: number, next: number, alpha: number): number {
    return prev === 0 ? next : prev + alpha * (next - prev);
}

export interface ProgressTrackerOptions {
    metricStates: MetricStates;
    submitPhaseSnapshot: (
        snapshot: PhaseCompletePayload,
        status: "partial" | "complete",
    ) => void;
}

export interface ProgressTracker {
    /** Reactive snapshot of the current test state. Null when idle. */
    data: Ref<LiveTestData | null>;
    /** Apply a progress tick — EMA-smooth + transition metric states +
     *  fire boundary callbacks as needed. */
    updateFromProgress: (progress: ProgressData) => void;
    /** Reset all smoothing + boundary-fire bookkeeping. Called between runs. */
    reset: () => void;
    /** Phase-boundary firing latches (subscription module reads to fire
     *  any remaining boundaries on completion). */
    readonly phaseBoundaryFired: { ping: boolean; download: boolean; upload: boolean };
    /** Mark a phase boundary fired (used by subscription module on resolution). */
    markPhaseBoundaryFired: (phase: "ping" | "download" | "upload") => void;
}

export function createProgressTracker(options: ProgressTrackerOptions): ProgressTracker {
    const { metricStates, submitPhaseSnapshot } = options;

    const data: Ref<LiveTestData | null> = ref(null);

    /** Smoothed speed values — EMA prevents wild oscillation in the UI. */
    const smoothedDownload = ref(0);
    const smoothedUpload = ref(0);

    /** Boundary-fire bookkeeping: each phase fires its boundary at most once
     *  per test run; reset in `reset()`. */
    const phaseBoundaryFired = {
        ping: false,
        download: false,
        upload: false,
    };

    function updateFromProgress(progress: ProgressData): void {
        const prev = data.value;

        if (progress.phase === "download" && progress.speed > 0) {
            smoothedDownload.value = ema(smoothedDownload.value, progress.speed, EMA_ALPHA);
        }
        if (progress.phase === "upload" && progress.speed > 0) {
            smoothedUpload.value = ema(smoothedUpload.value, progress.speed, EMA_ALPHA);
        }

        data.value = {
            phase: progress.phase,
            downloadSpeed:
                progress.phase === "download"
                    ? smoothedDownload.value
                    : (prev?.downloadSpeed ?? 0),
            uploadSpeed:
                progress.phase === "upload"
                    ? smoothedUpload.value
                    : (prev?.uploadSpeed ?? 0),
            ping: progress.ping ?? prev?.ping ?? 0,
            jitter: progress.jitter ?? prev?.jitter ?? 0,
            downloadProgress:
                progress.phase === "download"
                    ? progress.progress
                    : (prev?.downloadProgress ?? 0),
            uploadProgress:
                progress.phase === "upload"
                    ? progress.progress
                    : (prev?.uploadProgress ?? 0),
            pingProgress:
                progress.phase === "ping"
                    ? progress.progress
                    : (prev?.pingProgress ?? 0),
        };

        // Update per-metric lifecycle states + fire boundary callbacks.
        const phase = progress.phase;
        if (phase === "ping") {
            if (metricStates.ping === TestState.notStarted)
                metricStates.ping = TestState.started;
            else metricStates.ping = TestState.active;
        } else if (phase === "download") {
            if (metricStates.ping !== TestState.finished)
                metricStates.ping = TestState.finished;
            if (metricStates.download === TestState.notStarted)
                metricStates.download = TestState.started;
            else metricStates.download = TestState.active;

            // Phase boundary: ping → download. Fire once with the cumulative
            // ping/jitter snapshot; download/upload still null.
            if (!phaseBoundaryFired.ping) {
                phaseBoundaryFired.ping = true;
                const snap = data.value;
                submitPhaseSnapshot(
                    {
                        ping: snap?.ping ?? null,
                        jitter: snap?.jitter ?? null,
                        download: null,
                        upload: null,
                    },
                    "partial",
                );
            }
        } else if (phase === "upload") {
            if (metricStates.download !== TestState.finished)
                metricStates.download = TestState.finished;
            if (metricStates.upload === TestState.notStarted)
                metricStates.upload = TestState.started;
            else metricStates.upload = TestState.active;

            // Phase boundary: download → upload. Cumulative ping + download.
            if (!phaseBoundaryFired.download) {
                phaseBoundaryFired.download = true;
                const snap = data.value;
                submitPhaseSnapshot(
                    {
                        ping: snap?.ping ?? null,
                        jitter: snap?.jitter ?? null,
                        download: snap?.downloadSpeed ?? null,
                        upload: null,
                    },
                    "partial",
                );
            }
        }
    }

    function reset(): void {
        smoothedDownload.value = 0;
        smoothedUpload.value = 0;
        data.value = null;
        phaseBoundaryFired.ping = false;
        phaseBoundaryFired.download = false;
        phaseBoundaryFired.upload = false;
    }

    function markPhaseBoundaryFired(phase: "ping" | "download" | "upload"): void {
        phaseBoundaryFired[phase] = true;
    }

    return {
        data,
        updateFromProgress,
        reset,
        phaseBoundaryFired,
        markPhaseBoundaryFired,
    };
}
